// Helpers de rubro de marca/empresa.
//
// El rubro vive en `brand_profiles.rubro` y en `company_profiles.rubro` (text
// libre, alimentado por el select cerrado de `OnboardingSelects.tsx`). Hoy la
// comparación está duplicada como string literal en varios lugares
// (`VariantForm.tsx`, `GeneratedImagesInline.tsx`, `EscenaPersonalizadaOption.tsx`,
// `BibliotecaOption.tsx`). Este módulo existe para no sumar una duplicación más
// al agregar "Muebles" — ver ADR-585.
//
// No se refactorizaron los usos existentes a propósito: ensuciaría el diff de la
// rama del modo muebles. Cuando haya que tocarlos por otra razón, migrarlos acá.

export const RUBRO_MUEBLES = 'Muebles';

/**
 * Rubro que habilita los modos de muebles (`catalogo-muebles` e `infografia`)
 * — ver ADR-2026-07-31-modos-muebles-por-rubro-de-marca.
 *
 * Es el label EXACTO de `rubroOptions` (`components/OnboardingSelects.tsx`) y el
 * que escribe el onboarding nuevo (`categoria hogar` → este string, ver
 * `onboarding/accountSetup.ts`). El texto está duplicado en varios archivos como
 * literal; acá vive la única copia que decide gating de modos.
 */
export const RUBRO_HOGAR_DECO = 'Hogar y Decoración';

/**
 * `true` si la marca es del rubro Muebles.
 *
 * Es una comparación exacta contra el valor del select. `rubro` es text libre en
 * la base, así que alguien podría tener "muebles" en minúscula o "Mueblería"
 * escritos a mano y no matchear. Se acepta a sabiendas: la única consecuencia es
 * que no se le autocompletan las medidas al importar de Tienda Nube, y la puede
 * cargar a mano igual. Si el caso aparece seguido, normalizar acá.
 */
export const esRubroMuebles = (rubro?: string | null): boolean =>
  rubro === RUBRO_MUEBLES;

/**
 * `true` si el rubro es "Hogar y Decoración" — el gate de visibilidad de los
 * modos "Catálogo de muebles" e "Infografías" (ADR-2026-07-31-modos-muebles-por-rubro-de-marca).
 *
 * Se compara contra el rubro de la **marca activa** (`brand_profiles.rubro`),
 * NO contra el de la empresa: una misma cuenta puede tener una marca de deco y
 * otra de indumentaria, y los modos solo tienen sentido en la primera. Usar el
 * hook `useRubroMarcaActiva` para obtener ese valor.
 *
 * Comparación exacta, igual que `esRubroMuebles`: si la marca quedó con el
 * rubro vacío (`null`), los modos NO se muestran — es la decisión explícita
 * (nada de fallback al rubro de la empresa). El usuario lo destraba cargando el
 * rubro de la marca desde Configuración → Mi marca.
 *
 * El rubro legacy `Muebles` NO entra: el onboarding nuevo ya no lo ofrece
 * (no tiene card en el paso 2) y se decidió gatear solo por "Hogar y Decoración".
 *
 * NOTA DE MERGE (ADR-2026-08-05-un-solo-gate-de-rubro-para-los-modos): la rama del
 * rediseño había llegado a la MISMA regla por su cuenta, con otros nombres
 * (`RUBRO_HOGAR_DECORACION` / `rubroVeInfografia`). Se conservó esta versión —la de
 * `principal`— porque además del rubro define el sistema completo de gating
 * (`soloHogarDeco` / `ocultoEnHogarDeco` en `modesCatalog`), y los nombres del
 * rediseño se eliminaron en vez de dejar dos alias de lo mismo.
 */
export const esRubroHogarDeco = (rubro?: string | null): boolean =>
  rubro === RUBRO_HOGAR_DECO;

export const RUBRO_MODA = 'Moda e Indumentaria';

/**
 * Las **plantillas** son sólo para Moda — ADR-2026-08-03-plantillas-solo-para-moda.
 *
 * El catálogo de plantillas es de escenas armadas para indumentaria (poses, modelos,
 * fondos de moda), así que a los demás rubros no les servía y ocupaba una sección de
 * la home, un ítem de la sidebar y dos rutas.
 *
 * El rubro que manda es el de la MARCA ACTIVA (`brand_profiles.rubro`), con fallback
 * al de la empresa: una cuenta puede tener una marca de moda y otra que no, y lo que
 * se ofrece tiene que seguir a la marca con la que se está trabajando.
 *
 * Misma comparación exacta que `esRubroMuebles`, y con la misma salvedad: `rubro` es
 * texto libre en la base, así que un "moda" escrito a mano no matchea. Acá la
 * consecuencia es más visible que en muebles —no ve el catálogo— pero la alternativa
 * (normalizar de a poco) haría que un rubro escrito raro entre a veces sí y a veces
 * no según qué pantalla lo lea.
 */
export const rubroVePlantillas = (rubro?: string | null): boolean =>
  rubro === RUBRO_MODA;

/** La marca activa es de "Moda e Indumentaria": el rubro del modo Diseño de indumentaria
 *  (`soloModa` en el catálogo — ADR-2026-09-17-el-modo-diseno-es-solo-para-moda). */
export const esRubroModa = (rubro?: string | null): boolean => rubro === RUBRO_MODA;

/**
 * Label EXACTO del rubro de belleza en `rubroOptions` (`OnboardingSelects.tsx`).
 * Ojo con la capitalización: en el select es "Cuidado Personal" con mayúsculas, y
 * la comparación es exacta como en todos los demás gates de rubro de este archivo.
 */
export const RUBRO_BELLEZA = 'Belleza y Cuidado Personal';

/**
 * `true` si la marca es de Belleza y Cuidado Personal — el gate del modo
 * **Plantillas de Belleza** (ADR-2026-09-10-plantillas-belleza-modo-propio).
 *
 * Es un modo APARTE del de Moda, no el mismo con un rubro más: son otras
 * categorías (`templatesBellezaApi`), otras rutas y otras pantallas
 * (`PlantillasBelleza` / `ColeccionDetalleBelleza`), pensadas para ir divergiendo.
 * Por eso son dos predicados y no un `rubroVePlantillas` que devuelva `true` para
 * los dos: cada rubro ve SU modo y no el del otro.
 *
 * Mismo criterio de lectura que `rubroVePlantillas`: manda el rubro de la marca
 * ACTIVA (`brand_profiles.rubro`) con fallback al de la empresa, y la comparación
 * es exacta contra el valor del select, así que un rubro escrito a mano no matchea.
 */
export const rubroVePlantillasBelleza = (rubro?: string | null): boolean =>
  esRubroBelleza(rubro);

/**
 * `true` si la marca es del rubro "Belleza y Cuidado Personal" — el gate del flag
 * `soloBelleza` de `modesCatalog` (ADR-2026-09-10-modo-mostrar-como-funciona-belleza).
 *
 * Es el gemelo exacto de `esRubroHogarDeco`: la pregunta "¿de qué rubro es la marca
 * activa?", separada de la pregunta "¿qué modo le corresponde?". `rubroVePlantillasBelleza`
 * ahora delega acá y sigue existiendo con su nombre porque nombra otra cosa —qué ve el
 * rubro— y es lo que leen la sidebar, `/modos` y los sheets para elegir CUÁL de los dos
 * modos de Plantillas mostrar.
 *
 * Misma comparación exacta contra el valor del select que el resto de los gates de este
 * archivo, con la misma salvedad: un rubro escrito a mano no matchea.
 */
export const esRubroBelleza = (rubro?: string | null): boolean =>
  rubro === RUBRO_BELLEZA;

/**
 * `true` si la marca ve la sección **Moodboards** de Mi marca —
 * ADR-2026-09-13-moodboards-carpetas-de-referencia.
 *
 * Es el mismo rubro que `rubroVePlantillas` (Moda e Indumentaria) y a propósito NO
 * delega en ella: nombra otra pregunta. Plantillas es un catálogo de escenas que
 * arma Atlas; Moodboards son carpetas de referencias que sube el cliente. Que hoy
 * coincidan en el rubro es una coincidencia del presente —el día que las referencias
 * le sirvan también a Belleza, se abre acá sin tocar el catálogo de plantillas—, y
 * un alias de una sobre la otra haría que abrir una abriera la otra sin querer.
 *
 * Mismo criterio de lectura que los demás gates de este archivo: manda el rubro de la
 * marca ACTIVA (`brand_profiles.rubro`) con fallback al de la empresa, la comparación
 * es exacta contra el valor del select, y un rubro escrito a mano no matchea.
 */
export const rubroVeMoodboards = (rubro?: string | null): boolean =>
  rubro === RUBRO_MODA;

/**
 * `true` si la home de esta marca **abre con el Agente** en vez de con las cards de
 * "Elegí qué querés crear" — ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente.
 *
 * Los tres rubros que NO entran son los que tienen catálogo propio armado para ellos y
 * un camino de modos que les calza: Moda e Indumentaria (`rubroVePlantillas`), Belleza y
 * Cuidado Personal (`esRubroBelleza`) y Hogar y Decoración (`esRubroHogarDeco`). Para el
 * resto —gastronomía, servicios, tecnología, el rubro escrito a mano— la tira de modos
 * es una lista de herramientas pensadas para vender ropa, y la primera pregunta útil es
 * "¿qué necesitás?", que es la que contesta el Agente.
 *
 * Es la NEGACIÓN de los tres gates y no una lista propia a propósito: el día que un
 * rubro sume su catálogo, alcanza con darle su predicado y sale solo de acá.
 *
 * Un rubro vacío (`null`) cae del lado del Agente, que es el mismo trato que le dan los
 * otros gates de este archivo: sin rubro cargado, ninguno de los tres matchea. El valor
 * que se le pasa sale de `useRubroMarcaActiva`, que arranca con el rubro CACHEADO en
 * `localStorage`, así que la home no parpadea de una forma a la otra.
 */
export const homeAbreConElAgente = (rubro?: string | null): boolean =>
  !rubroVePlantillas(rubro) && !esRubroBelleza(rubro) && !esRubroHogarDeco(rubro);

/**
 * `true` si la home le PRESENTA el Agente a esta marca — la barra sin desplegar que va
 * arriba de "Elegí qué querés crear" (ADR-2026-09-22-la-barra-del-agente-es-la-presentacion-final,
 * supersede ADR-2026-09-21-la-home-de-moda-presenta-el-agente).
 *
 * Es exactamente la CONTRACARA de `homeAbreConElAgente`, y por eso se escribe como su
 * negación y no como una lista propia: los rubros sin catálogo propio ya abren la home
 * con el Agente desplegado, así que presentárselo sería anunciarles lo que están
 * mirando. Los otros tres —Moda e Indumentaria, Belleza y Cuidado Personal, y Hogar y
 * Decoración— abren con sus modos, que son las herramientas armadas para ellos, así que
 * el Agente es lo único nuevo que no tiene dónde verse.
 *
 * Nació sólo para Moda, como experimento de tres variantes. Quedó la barra y se abrió a
 * los otros dos rubros con catálogo; lo que cambia con el rubro es el COPY, y eso vive
 * con el componente (`COPY_POR_RUBRO` en `PresentacionDelAgente.tsx`) y no acá.
 *
 * Mismo criterio de lectura que el resto de los gates: manda el rubro de la marca ACTIVA
 * con fallback al de la empresa, comparación exacta contra el valor del select, y un
 * rubro vacío o escrito a mano NO ve la franja (cae del lado del Agente desplegado).
 */
export const homeMuestraPresentacionDelAgente = (rubro?: string | null): boolean =>
  !homeAbreConElAgente(rubro);

/**
 * `true` si la marca ve las **poses**: la pestaña Poses del selector de fuentes y de la
 * biblioteca del Agente — ADR-2026-10-19-hogar-y-decoracion-no-ve-poses.
 *
 * Las poses son posturas de una persona que lleva el producto puesto. En Hogar y
 * Decoración el producto es un mueble o un objeto, y la pestaña sólo agregaba ruido.
 * Es la NEGACIÓN de un rubro y no una lista de los que la ven: el resto de los rubros
 * (y una marca sin rubro) siguen viéndolas como hasta ahora.
 *
 * Mismo criterio de lectura que los demás gates: manda el rubro de la marca ACTIVA y la
 * comparación es exacta contra el valor del select.
 */
export const rubroVePoses = (rubro?: string | null): boolean => !esRubroHogarDeco(rubro);
